import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, input, output, viewChild } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
    faExclamationTriangle,
    faExternalLinkSquareAlt,
    faInfoCircle,
    faSearch,
    faTaxi,
    faWrench,
} from '@fortawesome/free-solid-svg-icons';
import { CarouselComponent, CarouselModule } from 'ngx-bootstrap/carousel';

import {
    PaceNotificationDetailCarousel,
    PaceNotificationMaterialItem,
    PaceNotificationParticipantItem,
} from './pace-notification-detail-carousel.interface';

/**
 * The material or participant carousel of a notification-details screen.
 *
 * Everything it renders comes from {@link PaceNotificationDetailCarousel}; it reports back the two
 * things the host has to act on — a material pickup and a participant's e-mail address.
 *
 * Paging is public API ({@link showPreviousSlide} / {@link showNextSlide}) so a host that detects
 * swipe gestures can drive it, keeping gesture handling out of this component.
 */
@Component({
    selector: 'pace-notification-detail-carousel',
    imports: [FontAwesomeModule, CarouselModule, NgTemplateOutlet],
    templateUrl: './pace-notification-detail-carousel.component.html',
    styleUrls: ['./pace-notification-detail-carousel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaceNotificationDetailCarouselComponent {
    public readonly carousel = input.required<PaceNotificationDetailCarousel>();

    public readonly materialPickupSelect = output<PaceNotificationMaterialItem>();
    public readonly participantEmailSelect = output<PaceNotificationParticipantItem>();

    public readonly faInfoCircle = faInfoCircle;
    public readonly faExternalLinkSquareAlt = faExternalLinkSquareAlt;
    public readonly faTaxi = faTaxi;
    public readonly faExclamationTriangle = faExclamationTriangle;
    public readonly faSearch = faSearch;
    public readonly faWrench = faWrench;

    private readonly carouselView = viewChild<CarouselComponent>('carouselView');

    public constructor() {
        // A new arrangement — another notification, or the columns re-sorted for a different screen
        // size — starts over at the first slide.
        effect(() => {
            this.carousel();
            const view = this.carouselView();
            if (view) {
                view.activeSlide = 0;
            }
        });
    }

    public showPreviousSlide(): void {
        const view = this.carouselView();
        if (!view) {
            return;
        }
        view.activeSlide ??= 0;
        view.previousSlide();
    }

    public showNextSlide(): void {
        const view = this.carouselView();
        if (!view) {
            return;
        }
        view.activeSlide ??= 0;
        view.nextSlide();
    }

    public selectSlide(index: number): void {
        this.carouselView()?.selectSlide(index);
    }

    public isActiveSlide(index: number): boolean {
        return this.carouselView()?.activeSlide === index;
    }

    /**
     * Narrows a carousel item for the template, which renders materials and participants apart.
     *
     * @param item
     */
    public asMaterial(
        item: PaceNotificationMaterialItem | PaceNotificationParticipantItem,
    ): PaceNotificationMaterialItem {
        return <PaceNotificationMaterialItem>item;
    }

    public asParticipant(
        item: PaceNotificationMaterialItem | PaceNotificationParticipantItem,
    ): PaceNotificationParticipantItem {
        return <PaceNotificationParticipantItem>item;
    }
}
